import { Injectable, Signal, computed, signal } from '@angular/core';

import { DA_STRINGS, EN_STRINGS, type PortalStrings } from './portal-strings';

export type PortalLocale = 'da' | 'en';

/** localStorage key, matching the `rekruto_*` convention the auth and theme engines use. */
export const LOCALE_STORAGE_KEY = 'rekruto_locale';

/**
 * Anything that is not exactly `'da'` or `'en'` resolves to `'da'` - Danish is the product
 * default (docs/10: surfaces ship da/en from day one, Danish first), so a corrupt, absent or
 * legacy value degrades to it rather than to a browser-derived preference.
 */
function resolveLocale(stored: string | null): PortalLocale {
  return stored === 'en' ? 'en' : 'da';
}

/**
 * Owns the portal's surface language (docs/10 section 7: `lang` follows the active surface
 * language). Same pattern as core's ThemeService: a signal, the persisted preference in
 * localStorage, and the service - not any component - owns the document-level attribute it
 * drives (`lang`, which feeds screen readers and hyphenation).
 *
 * Portal-local by design: the portal is the only anonymous candidate surface, and its string
 * table (docs/10 section 5's microcopy) lives beside it. The API-supplied da/en pairs
 * (disclosure, status chips, deadline, meter labels) travel with their payloads; this service
 * picks the variant and carries everything the API does not localize.
 */
@Injectable({ providedIn: 'root' })
export class LocaleService {
  readonly #locale = signal<PortalLocale>(resolveLocale(localStorage.getItem(LOCALE_STORAGE_KEY)));

  readonly locale = this.#locale.asReadonly();
  readonly strings: Signal<PortalStrings> = computed(() =>
    this.#locale() === 'en' ? EN_STRINGS : DA_STRINGS,
  );

  constructor() {
    this.#apply();
  }

  set(locale: PortalLocale): void {
    this.#locale.set(locale);
    localStorage.setItem(LOCALE_STORAGE_KEY, locale);
    this.#apply();
  }

  toggle(): void {
    this.set(this.#locale() === 'da' ? 'en' : 'da');
  }

  /** Picks the localized member an API payload carries (da/en pairs), falling back to da. */
  pick(localized: { da: string; en: string } | null | undefined): string {
    if (!localized) return '';
    return this.#locale() === 'en' ? localized.en : localized.da;
  }

  /** Picks the localized list member an API payload carries. */
  pickList(localized: { da: string[]; en: string[] } | null | undefined): string[] {
    if (!localized) return [];
    return this.#locale() === 'en' ? localized.en : localized.da;
  }

  #apply(): void {
    document.documentElement.setAttribute('lang', this.#locale());
  }
}
